import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { CSSProperties, FC, ReactNode, useRef, useState } from 'react';
import { Swiper, SwiperItem } from '../swiper';
import { numberPx2rem } from '../utils';
import { NoticeBar, NoticeBarProps } from './notice-bar';
import './style.less';

export type NoticeBarListProps = {
  height: number | string;
  className?: string;
  style?: CSSProperties;
  noticeContent: ReactNode[];
  prefix?: ReactNode;
  extra?: ReactNode;
  speed?: number;
  /**
   * 开始滚动的延迟，单位 ms
   * autoStart=false，无效
   */
  delay?: number;
  /**
   * 内容未达到最大宽度，显示时长
   */
  duration?: number;
};
type SwiperRefApi = { next: () => void; prev: () => void };

/**
 * 通知栏多条消息
 * @param props
 * @returns
 */
export const NoticeBarList: FC<NoticeBarListProps> = (props) => {
  const classPrefix = 'v-note-bar-list';
  const style = { height: numberPx2rem(props.height) };
  const [noticeBarStatusMap, setNoticeBarStatusMap] = useState<
    Record<string, NoticeBarProps['controlledStatus']>
  >({});
  const swiperRef = useRef<SwiperRefApi>();
  const delay = props.delay as number;

  const onCreated = hooks.useCallbackRef((instance) => {
    swiperRef.current = instance as SwiperRefApi;
  });

  const onNextPage = hooks.useCallbackRef(() => {
    swiperRef.current?.next();
  });

  const slideChanged = hooks.useCallbackRef((instance) => {
    const { rel, length } = instance.track.details;
    const prevIndex = rel === 0 ? length : rel - 1;
    setNoticeBarStatusMap({ [`${prevIndex}`]: 'stop' });
    setTimeout(() => {
      setNoticeBarStatusMap({ [`${rel}`]: 'start' });
    }, delay);
  });

  hooks.useTimeout(() => {
    setNoticeBarStatusMap({ '0': 'start' });
  }, delay);

  return (
    <div className={classNames(classPrefix, props.className)} style={{ ...style, ...props.style }}>
      {props.prefix ? <div className={`${classPrefix}-prefix`}>{props.prefix}</div> : null}
      <Swiper
        dot={false}
        option={{ vertical: true, created: onCreated, loop: true, slideChanged, drag: false }}
      >
        {props.noticeContent.map((item, index) => {
          return (
            <SwiperItem key={index}>
              <NoticeBar
                onAnimatingEnd={onNextPage}
                speed={props.speed}
                autoStart={false}
                loop={props.noticeContent.length === 1}
                controlledStatus={noticeBarStatusMap[`${index}`]}
                delay={props.delay}
                duration={props.duration}
              >
                {item}
              </NoticeBar>
            </SwiperItem>
          );
        })}
      </Swiper>
      {props.extra ? <div className={`${classPrefix}-extra`}>{props.extra}</div> : null}
    </div>
  );
};

NoticeBarList.defaultProps = {
  delay: 1000,
  duration: 2000,
};
